// getVipLevelLivestreamChatStyle.js
'use strict';
import { Collection } from 'immutable';
import createCachedSelector from '../resource/createCachedSelector.js';
import { getVipBenefitLevelThresholdByUserLevel } from '../selector/getUserLevelBenefitData.js';

/**
 * Select the user entered notice and chat log styles corresponding to the current user's VIP level
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {number} level - The user level to select the corresponding VIP level livestream chat style.
 * @param {number} minRequiredVipBenefitLevel - The minimum required VIP benefit level.
 * @param {number} minRequiredVipBenefitLevelIndex - The index of the minimum required VIP benefit level.
 * @param {Array} vipLevelLivestreamStyleList - The list of VIP level livestream styles.
 * @return {object} An object containing vipEnteredNoticeStyle and vipMessageLogStyle for the specified user level.
 */
const getVipLevelLivestreamChatStyle = createCachedSelector(
  (state, level) => {
    const { vipBenefitLevel } = getVipBenefitLevelThresholdByUserLevel(
      state,
      level
    );
    return vipBenefitLevel;
  },
  (state, level, minRequiredVipBenefitLevel) => minRequiredVipBenefitLevel,
  (state, level, minRequiredVipBenefitLevel, vipLevelLivestreamStyleList) =>
    vipLevelLivestreamStyleList,
  (level, minRequiredVipBenefitLevel, vipLevelLivestreamStyleList = []) => {
    const result = {
      vipMessageLogStyle: {},
      vipEnteredNoticeStyle: {},
    };

    // Result shape (styles are cached as strings to keep selector memoization stable):
    // {
    //   vipEnteredNoticeStyle: {
    //     4: "[\"backgroundColor:rgba(253,143,78,0.4)\",\"borderColor:#a8a8a8\",\"borderImage:level_chat_border_silver\"]",
    //     ...
    //   },
    //   vipMessageLogStyle: {
    //     4: "[\"backgroundColor:linear-gradient(90deg,rgba(142,63,47,0.24)0%,rgba(142,63,47,0)100%)\",\"borderColor:#FFAB8E\"]",
    //     ...
    //   }
    // }
    const { vipEnteredNoticeStyle, vipMessageLogStyle } =
      vipLevelLivestreamStyleList.reduce(
        (
          prev,
          [vipLevelLivestreamChatStyle, vipLevelLivestreamChatLogStyle],
          index
        ) => {
          const level = index + minRequiredVipBenefitLevel;
          prev.vipEnteredNoticeStyle[level] = vipLevelLivestreamChatStyle;
          prev.vipMessageLogStyle[level] = vipLevelLivestreamChatLogStyle;
          return prev;
        },
        {
          vipEnteredNoticeStyle: {},
          vipMessageLogStyle: {},
        }
      );

    if (Object.keys(vipEnteredNoticeStyle).includes(`${level}`)) {
      const [backgroundColor, borderColor] = JSON.parse(
        vipEnteredNoticeStyle[level]
      );

      result.vipEnteredNoticeStyle = {
        backgroundColor: backgroundColor?.split(':')?.[1],
        borderColor: borderColor?.split(':')?.[1],
      };
    }
    if (Object.keys(vipMessageLogStyle).includes(`${level}`)) {
      const [backgroundColor, borderColor] = JSON.parse(
        vipMessageLogStyle[level]
      );
      result.vipMessageLogStyle = {
        backgroundColor: backgroundColor?.split(':')?.[1],
        borderColor: borderColor?.split(':')?.[1],
      };
    }

    return result instanceof Collection ? result.toJS() : result;
  }
)(
  (
    state,
    level,
    minRequiredVipBenefitLevel,
    vipLevelLivestreamStyleList = []
  ) =>
    `${level}:${minRequiredVipBenefitLevel}:${vipLevelLivestreamStyleList.join('')}`
);

export default getVipLevelLivestreamChatStyle;
